/**
 * useOfflineStatus Hook
 * Detects online/offline status and provides reactive state.
 * Triggers sync of pending items when coming back online.
 *
 * AI-4714: Offline detection and UI indicators
 */

import { useState, useEffect, useCallback } from 'react';
import { syncPendingItems, getPendingSyncItems } from '@/lib/offline-cache';
import { API_URL } from '@/lib/config';
import { useAuth } from '@/contexts/AuthContext';

interface OfflineStatus {
  isOnline: boolean;
  pendingSyncCount: number;
  isSyncing: boolean;
  lastSyncResult: { synced: number; failed: number } | null;
  triggerSync: () => Promise<void>;
}

export function useOfflineStatus(): OfflineStatus {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState<{ synced: number; failed: number } | null>(null);
  const { getToken } = useAuth();

  // Track online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check pending sync count periodically
  useEffect(() => {
    async function checkPending() {
      const items = await getPendingSyncItems();
      setPendingSyncCount(items.length);
    }

    checkPending();
    const interval = setInterval(checkPending, 30000); // every 30s
    return () => clearInterval(interval);
  }, []);

  // Auto-sync when coming back online
  const triggerSync = useCallback(async () => {
    if (!isOnline || isSyncing) return;

    setIsSyncing(true);
    try {
      const result = await syncPendingItems(API_URL, async () => {
        return getToken ? await getToken() : null;
      });
      setLastSyncResult(result);

      // Refresh pending count
      const items = await getPendingSyncItems();
      setPendingSyncCount(items.length);
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, isSyncing, getToken]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && pendingSyncCount > 0) {
      triggerSync();
    }
  }, [isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isOnline,
    pendingSyncCount,
    isSyncing,
    lastSyncResult,
    triggerSync,
  };
}
